import React from 'react';
import './GuideComponents.css';

/**
 * Pasos de un asistente (spec 007). La guía no dibuja un stepper: se deriva de la "fila de ítem" (número en
 * círculo, título bold) y de la paginación (activa en primario tenue; alto contraste: amarillo con texto negro).
 * - Paso actual: círculo en primario, título bold, `aria-current="step"`.
 * - Paso hecho: círculo con ✓ (el estado no depende sólo del color) y, si `onStepClick`, es un botón para volver.
 * - Paso pendiente: círculo con borde y texto secundario.
 * Lista ordenada (`<ol>`): los lectores anuncian "paso 2 de 3"; los pasos hechos se alcanzan con Tab.
 */

export interface GuideStep {
  key: string;
  label: React.ReactNode;
  /** Texto secundario opcional debajo del título (13 / Regular). */
  description?: React.ReactNode;
}

export interface GuideStepperProps {
  steps: GuideStep[];
  /** Índice (0-based) del paso actual. */
  current: number;
  /** Si viene, los pasos ya hechos son botones para volver a ellos. */
  onStepClick?: (index: number) => void;
  /** Bloquea la navegación (p. ej. mientras se importa). */
  disabled?: boolean;
  ariaLabel: string;
  /** Texto accesible de un paso hecho ("completado"). */
  doneLabel?: string;
  className?: string;
  'data-testid'?: string;
}

export const GuideStepper = ({ steps, current, onStepClick, disabled, ariaLabel, doneLabel, className, ...rest }: GuideStepperProps) => (
  <nav aria-label={ariaLabel} className={['ichtys-stepper', className].filter(Boolean).join(' ')} data-testid={rest['data-testid']}>
    <ol className="ichtys-stepper__list">
      {steps.map((step, index) => {
        const state = index < current ? 'done' : index === current ? 'current' : 'pending';
        const content = (
          <>
            <span className="ichtys-stepper__marker" aria-hidden="true">
              {state === 'done' ? '✓' : index + 1}
            </span>
            <span className="ichtys-stepper__text">
              <span className="ichtys-stepper__label">{step.label}</span>
              {step.description && <span className="ichtys-stepper__description">{step.description}</span>}
            </span>
            {state === 'done' && doneLabel && <span className="ichtys-stepper__sr">{` (${doneLabel})`}</span>}
          </>
        );
        const clickable = state === 'done' && !!onStepClick && !disabled;
        return (
          <li
            key={step.key}
            className={`ichtys-stepper__step ichtys-stepper__step--${state}`}
            aria-current={state === 'current' ? 'step' : undefined}
            data-state={state}
            data-testid={rest['data-testid'] ? `${rest['data-testid']}-${step.key}` : undefined}
          >
            {clickable ? (
              <button type="button" className="ichtys-stepper__button" onClick={() => onStepClick?.(index)}>
                {content}
              </button>
            ) : (
              <span className="ichtys-stepper__button ichtys-stepper__button--static">{content}</span>
            )}
          </li>
        );
      })}
    </ol>
  </nav>
);

export default GuideStepper;
